import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FontText } from './FontText';
import { useTranslation } from 'react-i18next';
import { getFontSizeForScreen } from '../../utils/fontUtils';

interface BookStatsProps {
  readers: number;
  chapters: number;
  rating: number;
  ratingCount: number;
}

export const BookStats: React.FC<BookStatsProps> = ({
  readers,
  chapters,
  rating,
  ratingCount,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const styles: Record<string, React.CSSProperties> = {
    frame: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 8px',
      backgroundColor: theme.colors.surface,
      borderRadius: 10,
      marginBottom: 40,
      boxSizing: 'border-box',
      width: '100%',
    },
    statItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      flex: 1,
      alignItems: 'center',
      textAlign: 'center',
    },
    valueWrapper: {
      display: 'flex',
      justifyContent: 'center',
      gap: 8,
      alignItems: 'center',
      width: '100%',
    },
    ratingValueContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: 2,
      alignItems: 'center',
      width: '100%',
    },
    value: {
      color: theme.colors.text,
      whiteSpace: 'nowrap',
    },
    label: {
      color: theme.colors.textSecondary,
      width: '100%',
      textTransform: 'uppercase',
      fontSize: getFontSizeForScreen('tiny') * 0.8,
    },
    divider: {
      alignSelf: 'stretch',
      width: 1,
      backgroundColor: theme.colors.overlayLight,
    },
    starIcon: {
      width: 16,
      height: 16,
    },
  };

  const formatRating = (rating: number): string => {
    return rating.toFixed(1).replace('.', ',');
  };

  return (
    <div style={styles.frame}>
      <div style={styles.statItem}>
        <div style={styles.valueWrapper}>
          <FontText style={styles.value}>{readers.toLocaleString()}</FontText>
        </div>
        <FontText tiny style={styles.label}>
          {t('reading')}
        </FontText>
      </div>

      <div style={styles.divider} />

      <div style={styles.statItem}>
        <div style={styles.valueWrapper}>
          <FontText style={styles.value}>{chapters}</FontText>
        </div>
        <FontText tiny style={styles.label}>
          {t('chapters')}
        </FontText>
      </div>

      <div style={styles.divider} />

      <div style={styles.statItem}>
        <div style={styles.ratingValueContainer}>
          <span style={{ fontSize: 16 }}>⭐</span>
          <FontText style={styles.value}>{formatRating(rating)}</FontText>
        </div>
        <FontText tiny style={styles.label}>
          {ratingCount} {t('ratings')}
        </FontText>
      </div>
    </div>
  );
};
